import React, { useState, useEffect, useRef } from 'react';
import IntroductionAudio from '../IntroductionAudio';
import api from '../../../services/api';

const VoiceQuestionPage = ({ page, onNext }) => {
  const [recordingStates, setRecordingStates] = useState({}); 
  const [recordingTimes, setRecordingTimes] = useState({}); 
  const [processingStates, setProcessingStates] = useState({}); 
  const [answeredStates, setAnsweredStates] = useState({}); 
  const [correctStates, setCorrectStates] = useState({}); 
  const [attemptCounts, setAttemptCounts] = useState({}); 
  const recordingTimerRefs = useRef({});
  const mediaRecorderRefs = useRef({});
  const audioChunksRefs = useRef({});

  const questions = page.questions || [];
  const maxRecordingTime = page.maxRecordingTime || 10; 

  useEffect(() => {
    return () => {
      Object.values(recordingTimerRefs.current).forEach(timer => {
        if (timer) clearInterval(timer);
      });
      Object.values(mediaRecorderRefs.current).forEach(recorder => {
        if (recorder && recorder.state !== 'inactive') {
          recorder.stop();
        }
      });
    };
  }, []);

  useEffect(() => {
    questions.forEach((question, index) => {
      const questionId = question.id;
      if (recordingStates[questionId]) {
        if (!recordingTimerRefs.current[questionId]) {
          recordingTimerRefs.current[questionId] = setInterval(() => {
            setRecordingTimes(prev => {
              const currentTime = prev[questionId] || 0;
              if (currentTime >= maxRecordingTime) {
                handleStopRecording(questionId);
                return { ...prev, [questionId]: maxRecordingTime };
              }
              return { ...prev, [questionId]: currentTime + 1 };
            });
          }, 1000);
        }
      } else {
        if (recordingTimerRefs.current[questionId]) {
          clearInterval(recordingTimerRefs.current[questionId]);
          recordingTimerRefs.current[questionId] = null;
        }
      }
    });

    return () => {
      Object.values(recordingTimerRefs.current).forEach(timer => {
        if (timer) clearInterval(timer);
      });
    };
  }, [recordingStates, maxRecordingTime, questions]);

  const handleStartRecording = async (questionId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRefs.current[questionId] = mediaRecorder;
      audioChunksRefs.current[questionId] = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRefs.current[questionId].push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        handleSubmitRecording(questionId);
      };

      mediaRecorder.start(100); 
      setRecordingStates(prev => ({ ...prev, [questionId]: true }));
      setRecordingTimes(prev => ({ ...prev, [questionId]: 0 }));
    } catch (error) {
      alert('Unable to access microphone. Please check your permissions.');
    }
  };

  const handleStopRecording = (questionId) => {
    const mediaRecorder = mediaRecorderRefs.current[questionId];
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      if (mediaRecorder.state === 'recording') {
        mediaRecorder.requestData();
      }
      mediaRecorder.stop();
    }
    setRecordingStates(prev => ({ ...prev, [questionId]: false }));
  };

  const encodeWAV = (samples, sampleRate, numChannels) => {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    
    const writeString = (view, offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    const floatTo16BitPCM = (output, offset, input) => {
      for (let i = 0; i < input.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, input[i]));
        output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      }
    };
    
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * 2, true);
    view.setUint16(32, numChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, 'data');
    view.setUint32(40, samples.length * 2, true);
    floatTo16BitPCM(view, 44, samples);
    
    return buffer;
  };

  const handleSubmitRecording = async (questionId) => {
    if (!audioChunksRefs.current[questionId] || audioChunksRefs.current[questionId].length === 0) {
      return;
    }

    setProcessingStates(prev => ({ ...prev, [questionId]: true }));

    try {
      const webmBlob = new Blob(audioChunksRefs.current[questionId], { type: 'audio/webm' });
      
      let audioBlob;
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext({ sampleRate: 16000 });
        
        const arrayBuffer = await webmBlob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        const samples = audioBuffer.getChannelData(0);
        const sampleRate = audioBuffer.sampleRate;
        
        const wavBuffer = encodeWAV(samples, sampleRate, 1);
        audioBlob = new Blob([wavBuffer], { type: 'audio/wav' });
        
        await audioContext.close();
      } catch (conversionError) {
        audioBlob = webmBlob;
      }

      const isAzureQuestion = ['q4', 'q5', 'q6'].includes(questionId);
      
      let formData;
      if (isAzureQuestion) {
        const wordMapping = {
          'q4': 'aeiou',
          'q5': 'uoiea',
          'q6': 'eoiua'
        };
        
        const word = wordMapping[questionId];
        if (!word) {
          throw new Error(`No word mapping found for question ID: ${questionId}`);
        }

        formData = new FormData();
        formData.append('audio', audioBlob, 'recording.wav');
        formData.append('keys', JSON.stringify([word.toUpperCase()])); // Use word as key for Azure
        formData.append('word', word);
        formData.append('lesson', '4');
        formData.append('questionId', questionId);
      } else {
        const keyMapping = {
          'q1': 'P1',
          'q2': 'P2',
          'q3': 'P3'
        };
        
        const mlApiKey = keyMapping[questionId];
        if (!mlApiKey) {
          throw new Error(`No key mapping found for question ID: ${questionId}`);
        }

        formData = new FormData();
        formData.append('audio', audioBlob, 'recording.wav');
        formData.append('keys', JSON.stringify([mlApiKey]));
        formData.append('word', ''); 
        formData.append('lesson', '3'); 
        formData.append('questionId', questionId);
      }

      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('Audio recording is empty. Please try recording again.');
      }


      const response = await api.post('/voice-question/process', formData);

      const { is_correct } = response.data.data || response.data;
      
      const currentAttempt = (attemptCounts[questionId] || 0) + 1;
      setAttemptCounts(prev => ({ ...prev, [questionId]: currentAttempt }));
      
      setCorrectStates(prev => ({ ...prev, [questionId]: is_correct }));
      setProcessingStates(prev => ({ ...prev, [questionId]: false }));

      // Play feedback audio
      const audioUrl = is_correct 
        ? 'https://d3vx0tne0vxeo1.cloudfront.net/audios/chimes/correct.mp3'
        : 'https:/d3vx0tne0vxeo1.cloudfront.net/audios/chimes/wrong.mp3';
      const audio = new Audio(audioUrl);
      audio.play().catch(error => {
        console.error('Error playing feedback audio:', error);
      });
      
      if (is_correct) {
        setAnsweredStates(prev => {
          const updated = { ...prev, [questionId]: true };
          
          const allAnswered = questions.every(q => updated[q.id]);
          if (allAnswered && onNext) {
            setTimeout(() => onNext(), 1000); 
          }
          
          return updated;
        });
      } else {
        if (currentAttempt >= 2) {
          setAnsweredStates(prev => {
            const updated = { ...prev, [questionId]: true };
            
            const allAnswered = questions.every(q => updated[q.id]);
            if (allAnswered && onNext) {
              setTimeout(() => onNext(), 1000);
            }
            
            return updated;
          });
        }
      }
    } catch (error) {
      
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Error processing audio. Please try again.';
      
      alert(errorMessage);
      setProcessingStates(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const handleRetry = (questionId) => {
    setCorrectStates(prev => ({ ...prev, [questionId]: null }));
    setRecordingTimes(prev => ({ ...prev, [questionId]: 0 }));
    audioChunksRefs.current[questionId] = [];
  };

  const renderQuestion = (question, index) => {
    const questionId = question.id;
    const isRecording = recordingStates[questionId] || false;
    const recordingTime = recordingTimes[questionId] || 0;
    const isProcessing = processingStates[questionId] || false;
    const hasAnswered = answeredStates[questionId] || false;
    const isCorrect = correctStates[questionId];
    const attemptCount = attemptCounts[questionId] || 0;
    const canRetry = !isCorrect && attemptCount === 1 && !hasAnswered; 

    return (
      <div key={questionId} className="mb-6 flex items-center gap-4">
        <div className="flex-shrink-0">
          {isProcessing && (
            <div className="w-12 h-12 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
          {!isProcessing && !hasAnswered && (
            <>
              {!isRecording ? (
                <button
                  onClick={() => handleStartRecording(questionId)}
                  className="w-12 h-12 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors shadow-lg"
                  disabled={isProcessing}
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={() => handleStopRecording(questionId)}
                  className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center animate-pulse shadow-lg"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 012 0v4a1 1 0 11-2 0V7zm4 0a1 1 0 012 0v4a1 1 0 11-2 0V7z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </>
          )}
          {hasAnswered && isCorrect && (
            <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          {hasAnswered && !isCorrect && (
            <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 text-lg font-semibold text-gray-800">
          {index + 1}.
        </div>

        <div className="flex-1 flex items-center gap-4 flex-wrap">
          {question.svgs && Array.isArray(question.svgs) ? (
            question.svgs.map((svg, svgIndex) => (
              <img 
                key={svgIndex}
                src={typeof svg === 'string' ? svg : svg.url || svg} 
                alt={typeof svg === 'string' ? `Question ${index + 1} - Image ${svgIndex + 1}` : (svg.alt || `Question ${index + 1} - Image ${svgIndex + 1}`)}
                className="max-w-full h-auto"
                style={{ maxHeight: '100px' }}
              />
            ))
          ) : question.svg ? (
            <img 
              src={question.svg} 
              alt={`Question ${index + 1}`}
              className="max-w-full h-auto"
              style={{ maxHeight: '100px' }}
            />
          ) : question.images && Array.isArray(question.images) ? (
            question.images.map((image, imgIndex) => (
              <img 
                key={imgIndex}
                src={typeof image === 'string' ? image : image.url || image} 
                alt={typeof image === 'string' ? `Question ${index + 1} - Image ${imgIndex + 1}` : (image.alt || `Question ${index + 1} - Image ${imgIndex + 1}`)}
                className="max-w-full h-auto"
                style={{ maxHeight: '100px' }}
              />
            ))
          ) : question.image ? (
            <img 
              src={typeof question.image === 'string' ? question.image : question.image.url || question.image} 
              alt={typeof question.image === 'string' ? `Question ${index + 1}` : (question.image.alt || `Question ${index + 1}`)}
              className="max-w-full h-auto"
              style={{ maxHeight: '100px' }}
            />
          ) : question.text ? (
            <div className="text-xl font-semibold text-gray-800">
              {question.text}
            </div>
          ) : null}
        </div>


        {!isProcessing && !hasAnswered && isCorrect === false && canRetry && (
          <div className="flex-shrink-0 flex items-center gap-2">
            <div className="px-3 py-1 rounded-lg text-sm bg-red-50 text-red-800">
              Try again
            </div>
            <button
              onClick={() => handleRetry(questionId)}
              className="px-4 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors"
            >
              Retry
            </button>
          </div>
        )}
        {hasAnswered && (
          <div className={`flex-shrink-0 px-3 py-1 rounded-lg text-sm ${
            isCorrect 
              ? 'bg-green-50 text-green-800' 
              : 'bg-red-50 text-red-800'
          }`}>
            {isCorrect ? 'Correct!' : 'Incorrect'}
          </div>
        )}
      </div>
    );
  };

  if (!questions || questions.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-600">No questions available.</p>
      </div>
    );
  }

  const isAnyRecording = Object.values(recordingStates).some(state => state === true);
  const currentRecordingQuestion = questions.find(q => recordingStates[q.id]);
  const currentRecordingTime = currentRecordingQuestion ? (recordingTimes[currentRecordingQuestion.id] || 0) : 0;

  return (
    <div className="max-w-4xl mx-auto relative">
      {isAnyRecording && (
        <div className="fixed top-20 -right-2 z-50 bg-red-600 text-white px-8 py-3 rounded-lg shadow-lg min-w-[120px]">
          <div className="text-3xl font-bold text-center">
            {maxRecordingTime - currentRecordingTime}s
          </div>
        </div>
      )}

      {page.title && (
        <h2 className="text-2xl font-bold mb-4 text-gray-800">{page.title}</h2>
      )}

      {page.introductionAudio && (
        <div className="mb-6">
          {page.introductionAudio.description && (
            <p className="text-gray-700 mb-2">{page.introductionAudio.description}</p>
          )}
          <IntroductionAudio
            audio={page.introductionAudio}
            autoPlay={page.introductionAudio.autoPlay}
          />
        </div>
      )}

      <div className="space-y-4">
        {questions.map((question, index) => renderQuestion(question, index))}
      </div>
    </div>
  );
};

export default VoiceQuestionPage;

